import { useEffect, useState } from "react";
import { useLocation } from "@tanstack/react-router";
import { useAtom, useSetAtom } from "jotai";
import { cn, generateTextShadow } from "@/lib/utils";
import {
  checkGeolocation,
  isAllowedRoute,
  COUNTRY_NAMES,
} from "@/lib/geo-block";
import { geoCheckLoadingAtom, geoCheckResultAtom } from "@/lib/geoAtom";

const BYPASS_STORAGE_KEY = "cfl:geo-bypass";
const BYPASS_QUERY_PARAM = "GeoBypass";

export function GeoBlockOverlay() {
  const location = useLocation();
  const [geoResult, setGeoResult] = useAtom(geoCheckResultAtom);
  const setLoading = useSetAtom(geoCheckLoadingAtom);
  const [isBypassed, setIsBypassed] = useState(() => {
    if (typeof window === "undefined") return false;
    return localStorage.getItem(BYPASS_STORAGE_KEY) === "true";
  });

  // Handle bypass query param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get(BYPASS_QUERY_PARAM) === "1") {
      localStorage.setItem(BYPASS_STORAGE_KEY, "true");
      setIsBypassed(true);
      params.delete(BYPASS_QUERY_PARAM);
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params.toString()}`
        : window.location.pathname;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  // Run geo check on mount
  useEffect(() => {
    checkGeolocation().then((result) => {
      setGeoResult(result);
      setLoading(false);
    });
  }, [setGeoResult, setLoading]);

  if (
    isBypassed ||
    !geoResult?.isBlocked ||
    isAllowedRoute(location.pathname)
  ) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[9999] h-[100svh] text-white w-full flex flex-col overflow-x-hidden">
      <div
        className={cn(
          "relative flex-1 mx-auto w-full flex flex-col overflow-x-hidden bg-[#1D2A52]",
        )}
      >
        <div className="flex flex-1 flex-col items-center justify-center gap-2 mx-4 text-center">
          <img
            src="/assets/maintenance.png"
            className="h-auto w-68 sm:w-72"
            alt="Restricted"
          />

          <h1
            className="text-4xl sm:text-5xl text-[#cd4223] uppercase text-center sm:space-x-3"
            style={{
              fontFamily: "DeadRevolverDisplay",
              textShadow: generateTextShadow(0.4),
            }}
          >
            <span>Unsupported Region</span>
          </h1>

          <div className="text-base text-center flex flex-col gap-1 mx-2 max-w-lg">
            <p className="text-sm sm:text-base opacity-80">
              Sorry, CFL is currently not available in{" "}
              {geoResult?.country
                ? (COUNTRY_NAMES[geoResult.country] ?? geoResult.country)
                : "your region"}
              .
            </p>
            <p className="text-sm sm:text-base opacity-80">
              We apologize for the inconvenience.
            </p>
            <p className="text-xs sm:text-sm opacity-60 mt-4">
              If you believe this is a mistake, please try connecting to a
              different network and reopen the page.{" "}
            </p>
            <p className="text-xs sm:text-sm opacity-60 mt-2 sm:mt-0">
              Join our{" "}
              <a
                href="https://discord.com/invite/GQuP7ekfF9"
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-[#109ffe]"
              >
                Discord
              </a>{" "}
              for more updates
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
